import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SearchSubmitted } from '../../ampli';
import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { getAmpliSearchEventPayload, startNewSearchJourney } from '../../utils/ampli';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import { isClientNetworkError } from '../../utils/isClientNetworkError';
import isCoursePage from '../../utils/isCoursePage';
import { getRecentSearches } from '../../utils/recentSearches';
import { safeSessionStorage } from '../../utils/safeStorage';
import {
	searchFeedbackDialogEvents,
	searchFeedbackDialogStorageKeys,
} from '../../utils/searchFeedbackDialog';
import useRouter from '../../utils/useRouter';
import useSearchExperiments from '../../utils/useSearchExperiments';
import useSearchQuery from '../utils/useSearchQuery';
import SearchBarInputBox, { type Suggestion, SuggestionType } from './components/SearchBarInputBox';

interface SearchBarProps {
	closeDrawer?: () => void;
}

export enum SearchCategory {
	Library = 'Library',
	ProfessionalDevelopment = 'Professional Development',
}

const SearchBar: React.FC<SearchBarProps> = ({ closeDrawer = () => {} }) => {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState('');
	const [category, setCategory] = useState<SearchCategory>(SearchCategory.Library);
	const [searchSuggestions, setSearchSuggestions] = useState<Suggestion[]>([]);
	const suggestionsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isSubmittingSearchRef = useRef(false);
	// Tracks user typing vs URL-driven updates — prevents route transitions
	// from overwriting the user's input while they're actively editing.
	const isUserEditingRef = useRef(false);
	const user = useSelector(selectUser);
	const { pushSearchQueryToRouter } = useSearchQuery();
	const { match } = useRouter();
	const { isReady: areSearchExperimentsReady } = useSearchExperiments();

	useEffect(() => {
		if (isCoursePage(match.location.pathname)) {
			setCategory(SearchCategory.ProfessionalDevelopment);
		} else {
			setCategory(SearchCategory.Library);
		}
	}, [match.location.pathname]);

	const maxNumberOfSuggestions = 8;

	// Recent searches are read locally — no API call, no debounce needed.
	// Kept in its own effect so that async `user` ref changes (selectUser is a
	// derived selector that recomputes when any of its underlying slices —
	// subscription, experiments, etc. — hydrate) don't re-trigger the
	// suggestions API call below.
	useEffect(() => {
		if (inputValue) return;
		const recentSearches = user ? getRecentSearches(user.id) : [];
		const recentSearchesSuggestions = recentSearches
			.filter((search) => search.term)
			.map((search) => ({
				label: search.term as string,
				value: search.term as string,
				type: SuggestionType.RecentSearch,
			}))
			.slice(0, maxNumberOfSuggestions);
		setSearchSuggestions(recentSearchesSuggestions);
	}, [inputValue, user]);

	useEffect(() => {
		if (!inputValue) return;
		if (isSubmittingSearchRef.current) {
			isSubmittingSearchRef.current = false;
			return;
		}
		// Skip when the input was prefilled from the URL (or a category change)
		// rather than user typing — the user hasn't asked for suggestions and the
		// search results for this term are already being rendered.
		if (!isUserEditingRef.current) return;

		const abortController = new AbortController();
		suggestionsTimerRef.current = setTimeout(() => {
			const suggestionsContentType =
				category === SearchCategory.ProfessionalDevelopment ? ['course' as const] : undefined;
			services.mindItem
				.getSearchSuggestions(inputValue, suggestionsContentType, abortController.signal)
				.then((searchSuggestionsResponseData) => {
					const suggestions = searchSuggestionsResponseData
						.map((suggestion) => ({
							label: suggestion.text,
							value: suggestion.text,
							type: SuggestionType.SearchSuggestion,
						}))
						.slice(0, maxNumberOfSuggestions);
					setSearchSuggestions(suggestions);
				})
				.catch((error: unknown) => {
					if (error instanceof DOMException && error.name === 'AbortError') return;
					if (error instanceof Error && isClientNetworkError(error.message)) {
						notifyBugsnagHandledError(error);
					}
				});
		}, 300);

		return () => {
			abortController.abort();
			if (suggestionsTimerRef.current) {
				clearTimeout(suggestionsTimerRef.current);
				suggestionsTimerRef.current = null;
			}
		};
	}, [inputValue, category]);

	const urlSearchTerm = match.location.query?.['term'] || '';
	useEffect(() => {
		if (isUserEditingRef.current) return;
		setInputValue(urlSearchTerm);
	}, [urlSearchTerm]);

	const handleSetInputValue = (value: string) => {
		isUserEditingRef.current = true;
		setInputValue(value);
	};

	const handleOnSearch = (term: string, searchSuggestionApplied = false) => {
		isSubmittingSearchRef.current = true;
		isUserEditingRef.current = false;

		if (suggestionsTimerRef.current) {
			clearTimeout(suggestionsTimerRef.current);
			suggestionsTimerRef.current = null;
		}

		const submittedAt = Date.now().toString();
		safeSessionStorage.setItem(searchFeedbackDialogStorageKeys.submittedAt, submittedAt);
		if (term) {
			safeSessionStorage.setItem(searchFeedbackDialogStorageKeys.searchQuery, term);
		} else {
			safeSessionStorage.removeItem(searchFeedbackDialogStorageKeys.searchQuery);
		}
		if (user) {
			const searchId = startNewSearchJourney({
				searchSuggestionApplied,
				relatedTopicsApplied: false,
			});
			try {
				const searchPayload = getAmpliSearchEventPayload({
					query: match.location.query,
					term,
					appSection: category === SearchCategory.Library ? 'library' : 'courses',
					searchSuggestionApplied,
					relatedTopicsApplied: false,
					searchId,
				});

				if (searchPayload) {
					const event = new SearchSubmitted(searchPayload as any);
					services.track.ampliEventTrack({ event });
				}
			} catch (error) {
				notifyBugsnagHandledError(error as Error);
			}
		}
		window.dispatchEvent(new Event(searchFeedbackDialogEvents.submitted));

		closeDrawer();

		pushSearchQueryToRouter({ term }, category);
	};

	const handleSetCategory = (newCategory: SearchCategory) => {
		if (newCategory === category) return;
		// Mirror handleOnSearch's submit cleanup so the category change doesn't trigger
		// a stray suggestions fetch (the suggestions effect has `category` as a dep) and
		// any pending debounced suggestion timer is cancelled before navigation.
		isSubmittingSearchRef.current = true;
		isUserEditingRef.current = false;
		if (suggestionsTimerRef.current) {
			clearTimeout(suggestionsTimerRef.current);
			suggestionsTimerRef.current = null;
		}
		setCategory(newCategory);
		// pushSearchQueryToRouter drops filter query params when crossing sections,
		// so stale filters from the previous section don't carry over.
		pushSearchQueryToRouter({ term: inputValue }, newCategory);
	};

	return (
		<SearchBarInputBox
			suggestions={searchSuggestions}
			placeholder={t('Search for topics, e.g., poems or fractional arithmetic.')}
			onSearch={handleOnSearch}
			inputValue={inputValue}
			setInputValue={handleSetInputValue}
			category={category}
			setCategory={handleSetCategory}
			disabled={!areSearchExperimentsReady}
		/>
	);
};

export default SearchBar;
